import { z } from 'zod';
import type { lotusHealthSchemas } from '@lotushealth/lotushealth_js_sdk';

import { logger } from '@/shared/lib';

export const AGENT_TWEAKABLE_TOOL_NAMES = [
  'updateCheckbox',
  'updateTextField',
  'updateSelect',
  'clickButton',
] as const;

export type AgentTweakableToolName = (typeof AGENT_TWEAKABLE_TOOL_NAMES)[number];

export type AgentTweakableStateTool<T> = {
  name: AgentTweakableToolName;
  description: string;
  zodType: z.ZodType<T>;
};

export type AgentTweakableStateItem<T> = {
  key: string;
  value: T;
  setValue: ((value: T) => unknown) | null;
};

export type AgentTweakableStateToolAndItems<T> = {
  tool: AgentTweakableStateTool<T>;
  items: Record<string, AgentTweakableStateItem<T>>;
};

export type AgentTweakableToolDefinition = lotusHealthSchemas.HostageToolDefinitionT;

export type AgentTweakableToolCall = {
  key: string;
  value: unknown;
};

export type AgentTweakableFrontendContextEntry = {
  key: string;
  line: string;
};

export type AgentTweakableFrontendContext = {
  entries: AgentTweakableFrontendContextEntry[];
  text: string;
  lineCount: number;
  keys: string[];
  keySample: string[];
};

function* candidateKeys(keyBase: string): Generator<string> {
  // results in keyBase, keyBase (2), keyBase (3), ...
  yield keyBase;
  for (let i = 2; ; i++) {
    yield `${keyBase} (${i})`;
  }
}

export class AgentTweakableState {
  tools: Record<string, AgentTweakableStateToolAndItems<unknown>>;

  constructor() {
    this.tools = {};
  }

  #selectKey<T>(keyBase: string, statePart: AgentTweakableStateToolAndItems<T>): string {
    for (const possibleKey of candidateKeys(keyBase)) {
      if (statePart.items[possibleKey]) {
        continue;
      }
      return possibleKey;
    }
    throw new Error(`No key found for ${keyBase}`);
  }

  registerItem<T>(
    tool: AgentTweakableStateTool<T>,
    keyBase: string,
    value: T,
    setValue: ((value: T) => unknown) | null
  ) {
    let statePart = this.tools[tool.name] as AgentTweakableStateToolAndItems<T> | undefined;
    if (!statePart) {
      statePart = {
        tool,
        items: {},
      };
      this.tools[tool.name] = statePart as AgentTweakableStateToolAndItems<unknown>;
    }
    const key = this.#selectKey(keyBase, statePart);
    if (!key) {
      throw new Error(`No key found for ${keyBase}`);
    }
    statePart.items[key] = {
      key,
      value,
      setValue,
    };

    logger.debug('copilot.agentTweakable.state.registerItem', {
      tool: tool.name,
      key,
      settable: setValue != null,
      valueType: typeof value,
    });

    return () => {
      if (!statePart || !statePart.items[key]) {
        throw new Error(`item ${key} mysteriously disappeared from ${tool.name}; can't deregister`);
      }
      delete statePart.items[key];
      logger.debug('copilot.agentTweakable.state.registerItem.#deregister', {
        tool: tool.name,
        key,
      });
    };
  }

  getFrontendTools(): AgentTweakableToolDefinition[] {
    const tools: AgentTweakableToolDefinition[] = [];
    let toolCount = 0;
    let itemCount = 0;
    for (const [, tool] of Object.entries(this.tools)) {
      toolCount += 1;
      const writableKeys: string[] = [];

      for (const item of Object.values(tool.items)) {
        itemCount += 1;
        if (item.setValue) {
          writableKeys.push(item.key);
        }
      }

      tools.push({
        name: tool.tool.name,
        // NOTE: Live "current state" is injected via prompt_fragment (getFrontendContextText),
        // not duplicated here in tool descriptions.
        description: tool.tool.description,
        input_schema: {
          type: 'object',
          properties: {
            key: {
              type: 'string',
              description: 'The key of the item to update',
              enum: writableKeys,
            },
            value: z.toJSONSchema(tool.tool.zodType as z.ZodType<unknown>) as Record<
              string,
              unknown
            >,
          },
          required: ['key', 'value'],
        },
      });
    }

    logger.debug('copilot.agentTweakable.state.getFrontendTools', {
      toolCount,
      itemCount,
      returnedToolDefinitions: tools.length,
    });
    return tools;
  }

  getFrontendContextEntries(): AgentTweakableFrontendContextEntry[] {
    return this.getFrontendContext().entries;
  }

  getFrontendContext(): AgentTweakableFrontendContext {
    const entries: AgentTweakableFrontendContextEntry[] = [];
    const readOnlyItems: { key: string; value: string }[] = [];
    const readWriteFieldItems: { tool: string; key: string; value: string }[] = [];
    const clickableActionItems: { tool: string; key: string; value: string }[] = [];

    for (const [, tool] of Object.entries(this.tools)) {
      for (const item of Object.values(tool.items)) {
        const fullKey = `${tool.tool.name}.${item.key}`;
        entries.push({
          key: fullKey,
          line: `${fullKey} = ${String(item.value)}${item.setValue ? '' : ' (read-only)'}`,
        });

        if (!item.setValue) {
          readOnlyItems.push({
            key: item.key,
            value: String(item.value),
          });
          continue;
        }

        const toolName = tool.tool.name;
        const bucket = toolName === 'clickButton' ? clickableActionItems : readWriteFieldItems;
        bucket.push({
          tool: toolName,
          key: item.key,
          value: String(item.value),
        });
      }
    }

    const lines: string[] = [];
    if (readOnlyItems.length > 0) {
      lines.push('## READ-ONLY UI STATE');
      for (const item of readOnlyItems) {
        const valuePreview = item.value.length > 120 ? `${item.value.slice(0, 120)}…` : item.value;
        lines.push(`- ${item.key}: ${valuePreview}`);
      }
    }

    if (readWriteFieldItems.length > 0) {
      if (lines.length > 0) lines.push('');
      lines.push('## EDITABLE CONTROLS (READ/WRITE)');
      for (const item of readWriteFieldItems) {
        lines.push(`- ${item.tool}(key="${item.key}", current="${item.value}")`);
      }
    }

    if (clickableActionItems.length > 0) {
      if (lines.length > 0) lines.push('');
      lines.push('## CLICKABLE CONTROLS (ACTIONS)');
      for (const item of clickableActionItems) {
        lines.push(`- ${item.tool}(key="${item.key}", current="${item.value}")`);
      }
    }

    const keys = entries.map((e) => e.key);
    return {
      entries,
      text: lines.join('\n'),
      lineCount: lines.length,
      keys,
      keySample: keys.slice(0, 10),
    };
  }

  getFrontendContextText(): string {
    const ctx = this.getFrontendContext();
    logger.debug('copilot.agentTweakable.state.getFrontendContextText', {
      lineCount: ctx.lineCount,
    });
    return ctx.text;
  }

  private _fail(msg: string): { handled: true; result: null; errorText: string } {
    return { handled: true, result: null, errorText: msg };
  }

  private _formatKeys(keys: string[]): string {
    return keys.map((k) => `"${k}"`).join(', ');
  }

  handleToolCall(
    name: string,
    call: AgentTweakableToolCall
  ): { handled: boolean; result: unknown; errorText?: string } {
    const tool = this.tools[name];
    if (!tool) return { handled: false, result: null };

    const { key, value } = call;
    const writableKeys = Object.values(tool.items)
      .filter((item) => item.setValue !== null)
      .map((item) => item.key);

    if (!key)
      return this._fail(
        `INVALID_KEY: "key" is required. Writable keys: [${this._formatKeys(writableKeys)}]`
      );
    if (value === undefined) return this._fail(`INVALID_VALUE: "value" is required for "${key}".`);

    const toolItem = tool.items[key];
    if (!toolItem)
      return this._fail(
        `KEY_NOT_FOUND: "${key}" not found. Available: [${this._formatKeys(Object.keys(tool.items))}]`
      );
    if (!toolItem.setValue) return this._fail(`READ_ONLY: "${key}" cannot be modified.`);

    const parsed = (tool.tool.zodType as z.ZodType<unknown>).safeParse(value);
    if (!parsed.success)
      return this._fail(
        `TYPE_ERROR: "${key}": ${parsed.error.issues.map((i) => i.message).join('; ')}`
      );

    return { handled: true, result: toolItem.setValue(parsed.data) ?? null };
  }

  tryHandleToolCall(
    name: string,
    call: AgentTweakableToolCall
  ): { handled: boolean; result: unknown; errorText?: string } {
    const result = this.handleToolCall(name, call);
    const level = result.errorText ? 'warn' : 'info';
    logger[level](
      `copilot.agentTweakable.state.toolCall.${result.errorText ? 'failure' : 'success'}`,
      {
        tool: name,
        key: call.key,
        valueType: typeof call.value,
        ...(result.errorText ? { error: result.errorText } : {}),
      }
    );
    return result;
  }
}

// == Example tool types ==
export const CheckboxTool: AgentTweakableStateTool<boolean> = {
  name: 'updateCheckbox',
  description: 'Click on a checkbox, setting it or unsetting it',
  zodType: z.boolean(),
};
export const TextFieldTool: AgentTweakableStateTool<string> = {
  name: 'updateTextField',
  description: 'Update the text in a text field',
  zodType: z.string(),
};
export const SelectTool: AgentTweakableStateTool<string> = {
  name: 'updateSelect',
  description:
    'Select a value for a constrained field (e.g. an enum). Provide exactly one option string; the UI will reject invalid options for that field.',
  zodType: z.string().trim().min(1),
};
export const ButtonTool: AgentTweakableStateTool<boolean> = {
  name: 'clickButton',
  description: 'Click on a button. (Set to true to click)',
  zodType: z.boolean(),
};
